import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Bell, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useAuth } from '@/contexts/AuthContext';
import { podeAbrirRota } from '@/app/registry/routes';
import { usePainelFiscal } from '@/hooks/tributacao/usePainelFiscal';
import type { Nivel } from '@/lib/fiscal/painel';
import { cn } from '@/lib/utils';

const PONTO: Record<Nivel, string> = { critico: 'bg-red-600', alto: 'bg-red-500', medio: 'bg-amber-500' };
const VISIVEIS = 6;

/**
 * Sino do cabeçalho: mostra quantos pontos do Painel fiscal precisam de atenção e os principais, cada um levando à
 * tela certa. Antes o sino não fazia nada ao clicar (28/10/2026). Só aparece para quem pode abrir o Painel.
 */
const SinoAlertas: React.FC = () => {
  const { hasPermission } = useAuth();
  const podeVer = podeAbrirRota('/tributacao/painel', hasPermission);
  const { painel } = usePainelFiscal({ enabled: podeVer });
  const [aberto, setAberto] = useState(false);

  if (!podeVer) return null;

  const alertas = painel?.alertas ?? [];
  const corrigir = alertas.filter((a) => a.nivel !== 'medio').length;
  const total = alertas.length;

  return (
    <Popover open={aberto} onOpenChange={setAberto}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="relative hidden h-10 w-10 rounded-md text-[var(--brand-ink-2)] hover:bg-white/10 hover:text-white sm:inline-flex"
          aria-label={total ? `${total} ponto(s) precisam de atenção` : 'Nada pendente'}
        >
          <Bell className="h-4 w-4" />
          {total > 0 ? (
            <span
              className={cn(
                'absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none text-white',
                corrigir ? 'bg-red-600' : 'bg-amber-500',
              )}
            >
              {total > 99 ? '99+' : total}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[22rem] p-0">
        <div className="border-b px-4 py-3">
          <div className="text-sm font-semibold">Precisa de atenção</div>
          <div className="text-xs text-muted-foreground">
            {total ? `${corrigir} para corrigir · ${total - corrigir} para revisar` : 'Do Painel fiscal'}
          </div>
        </div>
        {total === 0 ? (
          <div className="flex items-center gap-2 px-4 py-6 text-sm text-muted-foreground">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Nada pendente.
          </div>
        ) : (
          <ul className="max-h-80 divide-y overflow-y-auto">
            {alertas.slice(0, VISIVEIS).map((a) => (
              <li key={a.id}>
                <Link to={a.link} onClick={() => setAberto(false)} className="flex gap-2.5 px-4 py-2.5 hover:bg-muted/60">
                  <span className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', PONTO[a.nivel])} />
                  <span className="min-w-0">
                    <span className="block truncate text-xs font-semibold" title={a.cliente}>{a.cliente}</span>
                    <span className="line-clamp-2 text-sm">{a.texto}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
        <Link
          to="/tributacao/painel"
          onClick={() => setAberto(false)}
          className="flex items-center justify-between border-t px-4 py-2.5 text-sm font-medium text-primary hover:bg-muted/60"
        >
          {total > VISIVEIS ? `Ver todos os ${total} no Painel fiscal` : 'Abrir o Painel fiscal'}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </PopoverContent>
    </Popover>
  );
};

export default SinoAlertas;
